<style scoped>
.v-data-table__wrapper th:nth-child(4),
.v-data-table__wrapper td:nth-child(4) {
  min-width: 240px;
}

.v-data-table__wrapper th:nth-child(2),
.v-data-table__wrapper td:nth-child(2) {
  max-width: 200px;
}

.headline {
  background-color: #1b2643;
  width: 100%;
  height: 60px;
}

.headline p {
  color: #ffffff;
  font-size: 14px;
}

.dialog-table td:first-child {
  width: 180px;
  font-weight: 700 !important;
}

.dialog-campaign-table {
  margin-left: 8px;
}

.v-text-field--disabled .v-input__control {
  background-color: rgba(0, 0, 0, 0.38);
}

.v-card-title {
  font-size: 18px;
  font-weight: bold;
}

.v-card-text p {
  font-size: 16px;
  margin-bottom: 20px;
}

.v-btn {
  margin-right: 10px;
}

.content {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}
</style>

<template>
  <div>
	<div class="pa-3 headline">
      <p>{{ $t('editPackage.header') }}</p>
    </div>

    <div class="content d-flex flex-column pa-10" style="gap: 40px">
      <div style="gap: 10px">
        <v-btn
          color="#1b2643"
          class="white--text btn_shadow_half my-5"
          style="width: fit-content"
          @click="$router.back()"
        >
          {{ $t('common.back') }}
        </v-btn>
        <p class="text-h6">{{ $t('newPackage.subHeader') }}</p>

        <v-text-field
          v-model="packageCode"
          :label="$t('editPackage.packageCode')"
          disabled
          outlined
          persistent-placeholder
        ></v-text-field>

        <v-text-field
          v-model="packageName"
          :label="$t('newPackage.packageName')"
          :rules="[rules.required]"
          outlined
          persistent-placeholder
        ></v-text-field>

        <p class="text-body-2" v-html="$t('newPackage.coupon.text')"></p>

        <v-row>
          <v-col cols="7">
            <v-file-input
              v-model="csvFile"
              :error-messages="CSVInputErrorMessage"
              :label="$t('newPackage.coupon.couponFormat')"
              accept="text/csv"
              outlined
              persistent-placeholder
              @change="validateCSVInputFile"
            ></v-file-input>
          </v-col>

          <v-col class="mt-2" cols="5">
            <v-btn class="btn_shadow_half" @click="downloadCsvSample">
              <v-icon small>mdi-download</v-icon>
              {{ $t('newPackage.coupon.buttonText') }}
            </v-btn>
          </v-col>
        </v-row>

        <p class="text-body1">{{ $t('newPackage.viewingPeriod.text') }}</p>

        <PackageViewingPeriod
          :startDate="startViewingDate"
          :startTime="startViewingTime"
          :endDate="endViewingDate"
          :endTime="endViewingTime"
          :errorMessage="viewingPeriodErrorMessage"
          @update:startDate="startViewingDate = $event"
          @update:startTime="startViewingTime = $event"
          @update:endDate="endViewingDate = $event"
          @update:endTime="endViewingTime = $event"
          @validate="validateViewingPeriod"
        />

        <div class="pt-5">
          <p class="text-h6">{{ $t('newPackage.campaign.text') }}</p>

          <ModifiableCampaignTable
            :campaigns="editableCampaigns"
            :availableScenarios="availableScenarios"
            :rules="rules"
            :getScenarioDisplayName="getScenarioDisplayName"
            :startViewingDate="startViewingDate"
            :startViewingTime="startViewingTime"
            :endViewingDate="endViewingDate"
            :endViewingTime="endViewingTime"
            @update-campaign="onUpdateCampaignField"
            @move-up="moveUp"
            @move-down="moveDown"
            @delete-campaign="deleteCampaign"
            @create-campaign="createNewCampaign"
            @validate-period="onValidatePeriod"
            @same-as-viewing-period-change="onSameAsViewingPeriodChange"
          />
        </div>
      </div>

      <div class="d-flex justify-center pa-8">
        <v-dialog persistent v-model="editDialog" width="600">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :disabled="!isFormModified || isInputError"
              class="white--text mr-3"
              color="#1b2643"
              v-bind="attrs"
              v-on="on"
            >
              {{ $t('editPackage.edit') }}
            </v-btn>
          </template>
          <template v-slot:default="dialog">
            <EditPackageDialog
              :packageCode="packageCode"
              :packageName="packageName"
              :modifications="modifications"
              :isViewingPeriodFieldModified="isViewingPeriodFieldModified()"
              :viewingPeriodDateTime="viewingPeriodDateTime"
              :editableCampaigns="editableCampaigns"
              :isCampaignFieldModified="isCampaignFieldModified"
              :getScenarioNameById="getScenarioNameById"
              :isCampaignInterviewPeriodModified="
                isCampaignInterviewPeriodModified
              "
              :interviewDateTime="interviewDateTime"
              :importedCoupons="importedCoupons"
              :couponHeaders="couponHeaders"
              :itemsPerPageOptions="itemsPerPageOptions"
              :isEditing="isEditing"
              :errorMessage="editErrorMessage"
              @cancel="dialog.value = false"
              @submit="handleEditSubmit"
              @clearError="editErrorMessage = ''"
            />
          </template>
        </v-dialog>

        <v-btn
          class="white--text"
          color="#FF6160"
          @click="deletePackageConfirm"
        >
          <v-icon left>mdi-trash-can-outline</v-icon>
          {{ $t('editPackage.delete') }}
        </v-btn>
      </div>

      <!-- Delete Confirmation Modal -->
      <DeleteConfirmationDialog
        v-model="deleteDialog"
        :isDeleting="isDeleting"
        :deleteErrorSnackbar="deleteErrorSnackbar"
        :deleteErrorSnackbarText="deleteErrorSnackbarText"
        @cancel="deleteDialog = false"
        @confirm="confirmDelete"
        @closeSnackbar="deleteErrorSnackbar = false"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "@vue/composition-api";
import { agentGroupService } from "@/api/agentGroupService";
import { campaignService } from "@/api/campaignService";
import { packageCouponService } from "@/api/packageCouponService";
import { packageService } from "@/api/packageService";
import DeleteConfirmationDialog from "@/components/Dashboard/EditPackage/DeleteConfirmationDialog.vue";
import EditPackageDialog from "@/components/Dashboard/EditPackage/EditPackageDialog.vue";
import ModifiableCampaignTable from "@/components/Dashboard/EditPackage/ModifiableCampaignTable.vue";
import PackageViewingPeriod from "@/components/Dashboard/PageViewItems/PackageViewingPeriod.vue";
import { createIsoUtcDateTime } from "@/components/utils/dateUtils";
import type { AgentGroup } from "@/resources/agentGroup";
import type {
  CampaignField,
  CampaignWithId,
  IndexedCampaignWithId,
  ModifiableCampaignLineItem,
} from "@/resources/campaign";
import type { Coupon, CouponWithId } from "@/resources/coupon";
import type { PackageWithId } from "@/resources/package";
import type { ScenarioWithId } from "@/resources/scenario";
import {
  convertCampaignToModifiableCampaignLineItem,
  prepareForSaving,
  saveCoupons,
  saveExistingCampaigns,
  saveNewCampaigns,
} from "@/utils";
import { validateCSVInputFile } from "@/utils/csvUtils";
import { formatDateTimeRange, parseDateTime } from "@/utils/dateUtils";
import { downloadSampleCouponCsv } from "@/utils/downloadUtils";
import {
  validateCampaignInterviewPeriod,
  validateViewingPeriodFields,
} from "@/utils/editPackageUtils";
import { CouponCopyError } from "@/utils/errorUtils";
import { deletePackage, savePackageChanges } from "@/utils/packageUtils";
import {
  fetchScenarios,
  getScenarioDisplayName,
  getScenarioNameById,
} from "@/utils/scenarioUtils";

type ModifiableField =
  | "packageName"
  | "startViewingDate"
  | "startViewingTime"
  | "endViewingDate"
  | "endViewingTime";

interface EditPackageFormState {
  packageName: string;
  startViewingDate: string | null;
  startViewingTime: string;
  endViewingDate: string | null;
  endViewingTime: string;
  campaigns: ModifiableCampaignLineItem[];
}

interface Modifications {
  packageName: boolean;
  viewingPeriod: {
    startViewingDate: boolean;
    startViewingTime: boolean;
    endViewingDate: boolean;
    endViewingTime: boolean;
  };
  campaigns: Record<string, Record<string, boolean>>;
  newCampaigns: Set<number>;
  campaignOrderModified: boolean;
}

export default defineComponent({
  name: "EditPackage",
  components: {
    PackageViewingPeriod,
    ModifiableCampaignTable,
    EditPackageDialog,
    DeleteConfirmationDialog,
  },
  data() {
    return {
      // Dialog states
      editDialog: false,
      deleteDialog: false,
      isEditing: false,
      isDeleting: false,
      editErrorMessage: "" as string,

      deleteErrorSnackbar: false,
      deleteErrorSnackbarText: "",

      // Table headers
      couponHeaders: [
        {
          text: this.$t("editPackage.addNewCoupons.couponCode"),
          value: "couponCode",
          sortable: false,
        },
        {
          text: this.$t("editPackage.addNewCoupons.password"),
          value: "password",
          sortable: false,
        },
      ],
      itemsPerPageOptions: [10, 100, -1],

      // Read-only data
      currentPackage: null as PackageWithId | null,
      availableScenarios: [] as ScenarioWithId[],
      availableAgentGroups: [] as AgentGroup[],
      relatedCampaigns: [] as CampaignWithId[],
      allPackageCoupons: [] as Coupon[],

      // Form state
      csvFile: null as File | null,
      importedCoupons: [] as Partial<Coupon>[],

      packageCode: "" as string,
      packageName: "" as string,

      // Viewing period
      startViewingDate: null as string | null,
      startViewingTime: "" as string,
      endViewingDate: null as string | null,
      endViewingTime: "" as string,

      editableCampaigns: [] as ModifiableCampaignLineItem[],
      campaignIdsToDelete: [] as string[],

      initialFormState: {
        packageName: "",
        startViewingDate: "",
        startViewingTime: "",
        endViewingDate: "",
        endViewingTime: "",
        campaigns: [] as ModifiableCampaignLineItem[],
      } as EditPackageFormState,

      modifications: {
        packageName: false,
        viewingPeriod: {
          startViewingDate: false,
          startViewingTime: false,
          endViewingDate: false,
          endViewingTime: false,
        },
        campaigns: {} as Record<string, Record<string, boolean>>,
        newCampaigns: new Set<number>(),
        campaignOrderModified: false,
      } as Modifications,

      isFormModified: false,

      // Validation messages
      CSVInputErrorMessage: "" as string | null,
      viewingPeriodErrorMessage: "" as string | "",
    };
  },

  async mounted() {
    const packageId = this.$route.params.packageId as string;
    const packageData = await packageService.getByIdOrCode(packageId);
    if (!packageData) {
      this.$router.push("/dashboard/home");
      return;
    }

    this.currentPackage = packageData;

    if (this.$route.query.couponError === "true") {
      this.CSVInputErrorMessage = this.$t(
        "editPackage.validation.required",
      ) as string;
    }

    const packageCoupons = await this.fetchPackageCoupons();

    await this.initializeFormData(packageData, packageCoupons);
    const agentGroups = await agentGroupService.list();
    const scenarios = await fetchScenarios();
    const agentGroupsMap = Object.fromEntries(
      agentGroups.map((agentGroup) => [agentGroup.id, agentGroup]),
    );
    const scenariosWithValidAgentGroup = scenarios.filter(
      (scenario) => scenario.agentGroupId in agentGroupsMap,
    );
    this.availableScenarios = scenariosWithValidAgentGroup;
    this.availableAgentGroups = agentGroups;
  },

  watch: {
    packageName: {
      handler(newValue: string) {
        this.trackChange("packageName", newValue);
      },
      immediate: true,
    },

    startViewingDate: {
      handler(newValue: string) {
        this.trackChange("startViewingDate", newValue);
        this.editableCampaigns.forEach((c) => {
          if (c.isSameAsViewingPeriod) {
            this.$set(c, "startInterviewDate", newValue);
          }
        });
      },
      immediate: true,
    },

    startViewingTime: {
      handler(newValue: string) {
        this.trackChange("startViewingTime", newValue);
        this.editableCampaigns.forEach((c) => {
          if (c.isSameAsViewingPeriod) {
            this.$set(c, "startInterviewTime", newValue);
          }
        });
      },
      immediate: true,
    },

    endViewingDate: {
      handler(newValue: string) {
        this.trackChange("endViewingDate", newValue);
        this.editableCampaigns.forEach((c) => {
          if (c.isSameAsViewingPeriod) {
            this.$set(c, "endInterviewDate", newValue);
          }
        });
      },
      immediate: true,
    },

    endViewingTime: {
      handler(newValue: string) {
        this.trackChange("endViewingTime", newValue);
        this.editableCampaigns.forEach((c) => {
          if (c.isSameAsViewingPeriod) {
            this.$set(c, "endInterviewTime", newValue);
          }
        });
      },
      immediate: true,
    },

    editableCampaigns: {
      deep: true,
      handler(campaigns: ModifiableCampaignLineItem[]) {
        campaigns.forEach((campaign, index) => {
          if (!campaign.id) {
            this.modifications.newCampaigns.add(index);
            return;
          }

          this.trackCampaignChange(index, "name", campaign.name ?? "");
          this.trackCampaignChange(
            index,
            "scenarioDocId",
            campaign.scenarioDocId ?? "",
          );
          this.trackCampaignChange(
            index,
            "isDynamic",
            campaign.isDynamic ?? false,
          );

          const interviewFields = [
            "startInterviewDate",
            "startInterviewTime",
            "endInterviewDate",
            "endInterviewTime",
          ] as const;

          interviewFields.forEach((field) => {
            this.trackCampaignChange(index, field, campaign[field] ?? "");
          });

          this.trackCampaignChange(index, "limits", campaign.limits ?? null);
        });
        this.isFormModified = this.updateIsFormModified();
      },
      immediate: true,
    },

    modifications: {
      deep: true,
      handler() {
        this.isFormModified = this.updateIsFormModified();
      },
      immediate: true,
    },

    importedCoupons: {
      handler() {
        this.isFormModified = this.updateIsFormModified();
      },
      immediate: true,
    },

    campaignIdsToDelete: {
      handler() {
        this.isFormModified = this.updateIsFormModified();
      },
      immediate: true,
    },
  },

  computed: {
    rules() {
      return {
        required: (value: string) =>
          value ? true : String(this.$t("editPackage.validation.required")),
      };
    },

    isInputError(): boolean {
      if (this.packageName === "") return true;
      if (this.editableCampaigns.length === 0) return true;
      if (
        this.editableCampaigns.some(
          (campaign) =>
            !campaign.name ||
            (!campaign.isDynamic && !campaign.scenarioDocId) ||
            !campaign.startInterviewDate ||
            !campaign.startInterviewTime ||
            !campaign.endInterviewDate ||
            !campaign.endInterviewTime,
        )
      ) {
        return true;
      }
      if (
        this.editableCampaigns.some((campaign) => {
          this.validateViewingPeriod(campaign);
          return campaign.errorMessage !== "";
        })
      ) {
        return true;
      }
      if (this.viewingPeriodErrorMessage !== "") return true;
      if (this.CSVInputErrorMessage !== "") return true;
      return false;
    },

    viewingPeriodDateTime(): string {
      return formatDateTimeRange(
        this.startViewingDate,
        this.startViewingTime,
        this.endViewingDate,
        this.endViewingTime,
      );
    },
  },

  methods: {
    // ===== Lifecycle Methods =====
    async initializeFormData(
      packageData: PackageWithId,
      packageCoupons: CouponWithId[],
    ) {
      this.packageCode = packageData.code;
      this.packageName = packageData.name;

      const openAtParsed = parseDateTime(packageData.openAt);
      const closedAtParsed = parseDateTime(packageData.closedAt);

      this.startViewingDate = openAtParsed.date;
      this.startViewingTime = openAtParsed.time;
      this.endViewingDate = closedAtParsed.date;
      this.endViewingTime = closedAtParsed.time;

      this.initialFormState.packageName = this.packageName;
      this.initialFormState.startViewingDate = this.startViewingDate;
      this.initialFormState.startViewingTime = this.startViewingTime;
      this.initialFormState.endViewingDate = this.endViewingDate;
      this.initialFormState.endViewingTime = this.endViewingTime;

      this.allPackageCoupons = packageCoupons;

      const campaignIds = packageData.campaignIds;

      let campaigns = [] as CampaignWithId[];
      if (campaignIds.length > 0) {
        campaigns = await campaignService.list({
          ca: campaignIds,
        });
      }
      const packageCampaigns = campaigns
        .sort((a, b) => {
          // Sort by packageIndex (order in package's campaignIds array)
          const indexA = packageData.campaignIds.indexOf(a.id);
          const indexB = packageData.campaignIds.indexOf(b.id);
          return indexA - indexB;
        })
        .map((campaign, index) => ({
          ...campaign,
          index,
        }))
        .filter(
          (campaign): campaign is IndexedCampaignWithId => campaign !== null,
        );

      this.relatedCampaigns = packageCampaigns;

      this.editableCampaigns = packageCampaigns.map((campaign, index) =>
        convertCampaignToModifiableCampaignLineItem(
          campaign,
          index,
          packageData.openAt,
          packageData.closedAt,
        ),
      );
      // A package must always have at least one campaign row to edit.
      // If the package has none, seed an empty row so the user is forced
      // to fill it out before they can submit (matches NewPackage behavior).
      if (this.editableCampaigns.length === 0) {
        this.editableCampaigns.push({
          name: "",
          scenarioDocId: "",
          startInterviewDate: null,
          startInterviewTime: "",
          endInterviewDate: null,
          endInterviewTime: "",
          errorMessage: "",
          index: 0,
        });
      }
      this.initialFormState.campaigns = JSON.parse(
        JSON.stringify(this.editableCampaigns),
      );
      this.editableCampaigns.forEach((campaign) => {
        this.validateViewingPeriod(campaign);
      });
    },

    // ===== Data Fetching Methods =====

    async fetchPackageCoupons(): Promise<CouponWithId[]> {
      if (!this.currentPackage?.id) {
        return [];
      }
      return await packageCouponService.list(this.currentPackage.id);
    },

    // ===== Campaign Management Methods =====
    createNewCampaign() {
      this.editableCampaigns.push({
        name: "",
        scenarioDocId: "",
        isSameAsViewingPeriod: true,
        startInterviewDate: this.startViewingDate,
        startInterviewTime: this.startViewingTime,
        endInterviewDate: this.endViewingDate,
        endInterviewTime: this.endViewingTime,
        errorMessage: "",
        limits: 1,
        limitMenuOpen: false,
        index: this.editableCampaigns.length,
      });
    },

    deleteCampaign(index: number) {
      const shouldDelete = confirm(
        this.$t("editPackage.deleteCampaignConfirmationMessage") as string,
      );
      if (!shouldDelete) return;

      const campaignToDelete = this.editableCampaigns[index];
      if (campaignToDelete.id) {
        this.campaignIdsToDelete.push(campaignToDelete.id);
      }

      this.editableCampaigns.splice(index, 1);
    },

    swapCampaigns(index1: number, index2: number) {
      const campaign1 = this.editableCampaigns[index1];
      const campaign2 = this.editableCampaigns[index2];

      this.$set(this.editableCampaigns, index1, campaign2);
      this.$set(this.editableCampaigns, index2, campaign1);

      this.editableCampaigns[index1].index = index1;
      this.editableCampaigns[index2].index = index2;

      this.modifications.campaignOrderModified = true;
    },

    moveUp(index: number) {
      if (index > 0) {
        this.swapCampaigns(index, index - 1);
      }
    },

    moveDown(index: number) {
      if (index < this.editableCampaigns.length - 1) {
        this.swapCampaigns(index, index + 1);
      }
    },

    // ===== Data Transformation Methods =====

    determineCampaignOperation(
      campaign: ModifiableCampaignLineItem,
    ): "create" | "update" | "delete" | "none" {
      if (!campaign.id) return "create";

      const campaignMods = this.modifications.campaigns[campaign.index ?? 0];
      const hasModifications =
        (campaignMods && Object.values(campaignMods).some((v) => v)) ||
        this.isViewingPeriodFieldModified();
      const isRemovable = this.campaignIdsToDelete.includes(campaign.id);

      if (hasModifications) {
        return isRemovable ? "delete" : "update";
      }
      return "none";
    },

    // ===== Save/Update Methods =====
    async handleEditSubmit() {
      if (this.isEditing || !this.currentPackage) return;
      this.isEditing = true;
      this.editErrorMessage = "";
      const updatedAt = new Date().getTime();

      const campaignUpdates = this.editableCampaigns.map((campaign) =>
        prepareForSaving(campaign, this.determineCampaignOperation(campaign)),
      );

      try {
        await saveNewCampaigns(
          campaignUpdates,
          this.currentPackage,
          this.availableScenarios,
          this.getOpenAtClosedAtFromForm(),
        );
      } catch (error) {
        console.error("Error creating new campaigns:", error);
        this.editErrorMessage =
          error instanceof CouponCopyError
            ? (this.$t("editPackage.error.createCouponsFailed") as string)
            : (this.$t("editPackage.error.createCampaignsFailed") as string);
        this.isEditing = false;
        return;
      }

      try {
        await savePackageChanges(
          campaignUpdates,
          {
            ...this.currentPackage,
            name: this.packageName ?? this.currentPackage.name,
          },
          this.getOpenAtClosedAtFromForm(),
        );
      } catch (error) {
        console.error("Error saving package changes:", error);
        this.editErrorMessage = this.$t(
          "editPackage.error.updatePackageFailed",
        ) as string;
        this.isEditing = false;
        return;
      }

      try {
        await saveCoupons(
          this.currentPackage,
          this.relatedCampaigns,
          this.importedCoupons,
          updatedAt,
        );
      } catch (error) {
        console.error("Error creating coupons:", error);
        this.editErrorMessage = this.$t(
          "editPackage.error.createCouponsFailed",
        ) as string;
        this.isEditing = false;
        return;
      }

      try {
        await saveExistingCampaigns(
          campaignUpdates,
          this.getOpenAtClosedAtFromForm(),
        );
      } catch (error) {
        console.error("Error updating existing campaigns:", error);
        this.editErrorMessage = this.$t(
          "editPackage.error.updateCampaignsFailed",
        ) as string;
        this.isEditing = false;
        return;
      }

      try {
        await this.deleteRemovedCampaigns();
      } catch (error) {
        console.error("Error deleting campaigns:", error);
        this.editErrorMessage = this.$t(
          "editPackage.error.updateFailed",
        ) as string;
        this.isEditing = false;
        return;
      }

      this.isEditing = false;
      this.editDialog = false;
      this.$router.push({
        name: "Home",
        params: {
          snackbarAction: "true",
          snackbarText: this.$t("editPackage.success.updated") as string,
          snackbarColor: "blue",
          snackbarActionButtonText: this.$t(
            "newPackage.success.viewPackage",
          ) as string,
          snackbarScrollToSelector: `[data-package-id="${
            this.currentPackage?.id ?? ""
          }"]`,
        },
      });
    },

    async deleteRemovedCampaigns() {
      if (this.campaignIdsToDelete.length === 0) return;

      await Promise.all(
        this.campaignIdsToDelete.map(async (campaignId) => {
          await campaignService.deleteByIdOrCode(campaignId);
        }),
      );
    },

    // ===== Validation Methods =====
    validateCSVInputFile(file: File) {
      validateCSVInputFile(
        file,
        this.allPackageCoupons,
        (key) => this.$t(key) as string,
        (coupons) => {
          this.importedCoupons = coupons;
        },
        (msg) => {
          this.CSVInputErrorMessage = msg;
        },
      );
    },

    validateViewingPeriod(campaign: ModifiableCampaignLineItem) {
      if (!campaign) {
        this.editableCampaigns.forEach(this.validateViewingPeriod);
        return;
      }

      this.viewingPeriodErrorMessage = validateViewingPeriodFields(
        this.startViewingDate,
        this.startViewingTime,
        this.endViewingDate,
        this.endViewingTime,
        (key) => this.$t(key) as string,
      );

      if (campaign.isSameAsViewingPeriod) {
        campaign.errorMessage = "";
        return;
      }

      campaign.errorMessage = validateCampaignInterviewPeriod(
        {
          startInterviewDate: campaign.startInterviewDate ?? null,
          startInterviewTime: campaign.startInterviewTime ?? "",
          endInterviewDate: campaign.endInterviewDate ?? null,
          endInterviewTime: campaign.endInterviewTime ?? "",
        },
        this.startViewingDate,
        this.startViewingTime,
        this.endViewingDate,
        this.endViewingTime,
        (key) => this.$t(key) as string,
      );
    },

    // ===== Form State Tracking Methods =====
    updateIsFormModified(): boolean {
      if (this.modifications.packageName) return true;
      if (this.isViewingPeriodFieldModified()) return true;
      if (this.modifications.newCampaigns.size > 0) return true;
      if (Object.keys(this.modifications.campaigns).length > 0) return true;
      if (this.importedCoupons.length > 0) return true;
      if (this.modifications.campaignOrderModified) return true;
      if (this.campaignIdsToDelete.length > 0) return true;
      return false;
    },

    isFieldModified(field: ModifiableField, value: string): boolean {
      return this.initialFormState[field] !== value;
    },

    isCampaignFieldModified(index: number, field: CampaignField): boolean {
      if (this.modifications.newCampaigns.has(index)) return true;
      return this.modifications.campaigns[index]?.[field] ?? false;
    },

    isViewingPeriodFieldModified(): boolean {
      return Object.values(this.modifications.viewingPeriod).includes(true);
    },

    isCampaignInterviewPeriodModified(
      campaign: ModifiableCampaignLineItem,
    ): boolean {
      if (!campaign.id) return true;

      const index = this.editableCampaigns.findIndex((c) => c === campaign);
      const campaignMods = this.modifications.campaigns[index];
      if (!campaignMods) return false;

      return (
        campaignMods["startInterviewDate"] ||
        campaignMods["startInterviewTime"] ||
        campaignMods["endInterviewDate"] ||
        campaignMods["endInterviewTime"]
      );
    },

    trackChange(field: ModifiableField, newValue: string) {
      const isModified = this.isFieldModified(field, newValue);
      if (
        field === "startViewingDate" ||
        field === "startViewingTime" ||
        field === "endViewingDate" ||
        field === "endViewingTime"
      ) {
        this.modifications.viewingPeriod[field] = isModified;
        return;
      }

      if (field === "packageName") {
        this.modifications[field] = isModified;
      }
    },

    trackCampaignChange(
      index: number,
      field: CampaignField,
      newValue: string | number | boolean | null,
    ) {
      const originalCampaign = this.initialFormState.campaigns[index];
      if (!originalCampaign) return;

      if (!this.modifications.campaigns[index]) {
        this.$set(this.modifications.campaigns, index, {});
      }

      const originalValue = originalCampaign[field];
      if (originalValue !== newValue) {
        this.$set(this.modifications.campaigns[index], field, true);
      } else {
        this.$set(this.modifications.campaigns[index], field, false);

        if (
          Object.values(this.modifications.campaigns[index]).every((v) => !v)
        ) {
          this.$delete(this.modifications.campaigns, index);
        }
      }
    },

    // ===== Dialog Management Methods =====
    deletePackageConfirm() {
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (this.isDeleting) return;
      this.isDeleting = true;
      try {
        await deletePackage(
          this.currentPackage?.id ?? "",
          this.currentPackage?.campaignIds ?? [],
        );

        this.deleteDialog = false;
        this.$router.push("/dashboard/home");
      } catch (error) {
        console.error("Error deleting package:", error);
        this.deleteErrorSnackbarText = this.$t(
          "editPackage.deleteErrorSnackbarText",
          { internalError: `${(error as Error).message ?? ""}` },
        ) as string;
        this.deleteErrorSnackbar = true;
      } finally {
        this.isDeleting = false;
      }
    },

    // ===== Display Formatting Methods =====
    interviewDateTime(campaign: ModifiableCampaignLineItem): string {
      return formatDateTimeRange(
        campaign.startInterviewDate ?? "",
        campaign.startInterviewTime ?? "",
        campaign.endInterviewDate ?? "",
        campaign.endInterviewTime ?? "",
      );
    },

    onUpdateCampaignField({
      index,
      field,
      value,
    }: {
      index: number;
      field: string;
      value: string | boolean | null;
    }) {
      if (!this.editableCampaigns[index]) return;
      this.$set(this.editableCampaigns[index], field, value);
      if (field === "scenarioDocId") {
        const scenario = this.availableScenarios.find((s) => s.id === value);
        if (scenario) {
          const item = getScenarioDisplayName(
            scenario,
            this.availableAgentGroups,
          );
          this.$set(this.editableCampaigns[index], "name", item.scenarioName);
          this.$set(
            this.editableCampaigns[index],
            "agentGroupId",
            scenario.agentGroupId,
          );
        }
      }
    },

    onSameAsViewingPeriodChange({
      campaign,
      index,
    }: {
      campaign: ModifiableCampaignLineItem;
      index: number;
    }) {
      if (!this.editableCampaigns[index]) return;
      this.$set(this.editableCampaigns, index, { ...campaign });
    },

    onValidatePeriod({
      campaign,
      index,
    }: {
      campaign: ModifiableCampaignLineItem;
      index: number;
    }) {
      this.validateViewingPeriod(campaign);
      this.$set(this.editableCampaigns, index, { ...campaign });
    },

    getScenarioDisplayName(scenario: ScenarioWithId) {
      const item = getScenarioDisplayName(scenario, this.availableAgentGroups);
      return item.scenarioDisplayName;
    },

    getScenarioNameById(scenarioId: string | undefined) {
      return getScenarioNameById(
        scenarioId,
        this.availableScenarios,
        this.availableAgentGroups,
      );
    },

    getOpenAtClosedAtFromForm() {
      return {
        openAt: createIsoUtcDateTime(
          this.startViewingDate,
          this.startViewingTime,
        ),
        closedAt: createIsoUtcDateTime(
          this.endViewingDate,
          this.endViewingTime,
        ),
      };
    },

    downloadCsvSample() {
      downloadSampleCouponCsv();
    },
  },
});
</script>
